import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart drawer lives in the header section (not a separate cart-drawer section).
 * Section id comes from cart-items-component[data-section-id], e.g.
 * sections--30510208942425__header_section
 */
function getCartSectionId() {
  return document.querySelector('cart-items-component[data-section-id]')?.getAttribute('data-section-id') || '';
}

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

function clearCartDisabledState() {
  document.querySelectorAll('cart-items-component.cart-items-disabled').forEach((component) => {
    component.classList.remove('cart-items-disabled');
  });
}

function syncCartDrawerEmptyState(itemCount) {
  const isEmpty = !itemCount;
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (!(dialog instanceof HTMLElement)) return;

  dialog.classList.toggle('cart-drawer--empty', isEmpty);
  dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
}

function parseItemCountFromSections(sections, sectionId) {
  const html = sections?.[sectionId];
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const countEl = doc.querySelector('[ref="cartItemCount"]');
  if (!countEl?.textContent) return null;

  const parsed = parseInt(countEl.textContent, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Re-render the header section (contains the cart drawer) after cart mutations.
 */
async function refreshCartSection(sections, itemCount) {
  const sectionId = getCartSectionId();
  if (!sectionId) return;

  try {
    if (sections?.[sectionId]) {
      await morphSection(sectionId, sections[sectionId], { mode: 'full' });
    } else {
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' });
    }
  } catch (error) {
    console.error('Cart section refresh failed, refetching', error);
    await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' });
  }

  clearCartDisabledState();
  syncCartDrawerEmptyState(itemCount);
}

/**
 * @param {number} line - 1-based cart line index (Horizon convention)
 * @param {number} quantity
 */
export async function changeCartLine(line, quantity) {
  const sectionId = getCartSectionId();
  const sectionsToUpdate = getCartSectionIds();

  const body = JSON.stringify({
    line,
    quantity,
    sections: sectionsToUpdate.join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const parsed = await response.json();

  if (parsed.errors) {
    throw new Error(typeof parsed.errors === 'string' ? parsed.errors : 'Cart update failed');
  }

  let itemCount = parseItemCountFromSections(parsed.sections, sectionId);
  if (itemCount == null && typeof parsed.item_count === 'number') {
    itemCount = parsed.item_count;
  }
  if (itemCount == null) {
    itemCount = 0;
  }

  await refreshCartSection(parsed.sections, itemCount);

  document.dispatchEvent(
    new CartUpdateEvent(parsed, sectionId, {
      itemCount,
      source: 'marrow-cart',
      sections: parsed.sections,
    })
  );

  return parsed;
}

function handleRemoveClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-mw-cart-remove]');
  if (!button) return;

  if (!button.closest('cart-items-component')) return;

  event.preventDefault();
  event.stopPropagation();

  clearCartDisabledState();

  const line = parseInt(button.getAttribute('data-mw-cart-remove') || '', 10);
  if (!line) return;

  changeCartLine(line, 0).catch((error) => {
    console.error('Remove from cart failed', error);
  });
}

document.addEventListener('click', handleRemoveClick, true);

document.addEventListener(ThemeEvents.cartUpdate, () => {
  clearCartDisabledState();
});

window.MarrowCart = {
  changeLine: changeCartLine,
  clear: clearCartDisabledState,
};
